import { Theme } from '@mui/material/styles';
import { transitions, customShadows } from './insightTheme';

// Helper to access custom shadows from theme - Flat UI returns no shadows
export const getCustomShadow = (theme: Theme, type: keyof typeof customShadows) => {
  // Flat UI design - no shadows
  if (type === 'none') return 'none';
  const shadow = customShadows[type];
  if (typeof shadow === 'string') return shadow;
  return theme.palette.mode === 'dark' ? shadow.dark : shadow.light;
};

/**
 * Utility functions for applying consistent styling patterns across the application
 */

// Grey flat UI surface styling - NO glass morphism
export const glassMorphism = (theme: Theme) => ({
  background: theme.palette.mode === 'dark'
    ? '#1a1a1a'    // Grey flat dark primary - matches flatUIColors.background
    : '#ffffff',   // Pure white - matches flatUIColors.background
  border: theme.palette.mode === 'dark'
    ? '1px solid rgba(255,255,255,0.1)'
    : '1px solid rgba(0,0,0,0.1)',
  borderRadius: 0, // Sharp corners
  boxShadow: 'none',
});

// Grey flat UI enhanced surface - NO glass morphism
export const enhancedGlassMorphism = (theme: Theme) => ({
  background: theme.palette.mode === 'dark'
    ? '#1a1a1a'     // Grey flat dark primary
    : '#ffffff',    // Pure white
  border: theme.palette.mode === 'dark'
    ? '1px solid rgba(255, 255, 255, 0.1)'
    : '1px solid rgba(0, 0, 0, 0.1)',
  borderRadius: 0, // Sharp corners
  boxShadow: 'none',
});

// Flat UI hover effect - color only, no depth
export const hoverEffect = (theme: Theme) => ({
  transition: 'background-color 0.2s linear, border-color 0.2s linear',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.02)'
      : 'rgba(0, 0, 0, 0.01)',
    borderColor: theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.2)'
      : 'rgba(0, 0, 0, 0.2)',
  },
});

// Subtle hover effect for less prominent interactions - OPTIMIZED
export const subtleHoverEffect = (theme: Theme) => ({
  transition: transitions.gentle,
  cursor: 'pointer',
  '&:hover': {
    // REMOVED transform for performance: transform: 'translateY(-1px)',
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(0, 0, 0, 0.04)',
  },
});

// Green gradient for primary actions and success states
export const greenGradient = (theme: Theme) => ({
  background: theme.palette.mode === 'dark'
    ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.9) 0%, rgba(22, 163, 74, 0.8) 100%)'
    : 'linear-gradient(135deg, rgba(22, 163, 74, 1) 0%, rgba(34, 197, 94, 0.9) 100%)',
  color: '#ffffff',
  '&:hover': {
    background: theme.palette.mode === 'dark'
      ? 'linear-gradient(135deg, rgba(34, 197, 94, 1) 0%, rgba(22, 163, 74, 0.9) 100%)'
      : 'linear-gradient(135deg, rgba(22, 163, 74, 1) 0%, rgba(34, 197, 94, 1) 100%)',
  },
});

// Toggle button styling (used in ViewModeToggle, EarningsViewToggle, etc.)
export const toggleButton = (theme: Theme, isActive: boolean) => ({
  position: 'relative',
  zIndex: 2,
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  px: 1.5,
  py: 0.75,
  minWidth: 80,
  cursor: 'pointer',
  transition: transitions.standard,
  borderRadius: 2.5,
  '&:hover': {
    // REMOVED transform for performance: transform: !isActive ? 'translateY(-0.5px)' : 'none',
    opacity: !isActive ? 0.9 : 1,
  },
  ...(isActive && {
    ...greenGradient(theme),
    boxShadow: getCustomShadow(theme, 'elevated'),
    // REMOVED transform for performance: transform: 'translateY(-0.5px)',
  }),
});

// Toggle container styling - OPTIMIZED
export const toggleContainer = (theme: Theme) => ({
  display: 'inline-flex',
  background: theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.05)'
    : 'rgba(0, 0, 0, 0.04)',
  // REMOVED for performance: backdropFilter: 'blur(20px)',
  borderRadius: 3,
  padding: 0.25,
  border: theme.palette.mode === 'dark'
    ? '1px solid rgba(255, 255, 255, 0.1)'
    : '1px solid rgba(0, 0, 0, 0.08)',
  boxShadow: theme.palette.mode === 'dark'
    ? '0 2px 10px rgba(0, 0, 0, 0.3)'
    : '0 2px 10px rgba(0, 0, 0, 0.08)',
  position: 'relative',
  overflow: 'hidden',
});

// Table styling with sticky headers - OPTIMIZED
export const stickyTableHeader = (theme: Theme) => ({
  '& .MuiTableCell-head': {
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(35, 35, 35, 0.98)'
      : 'rgba(255, 255, 255, 0.98)',
    // REMOVED for performance: backdropFilter: 'blur(20px)',
    borderBottom: theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.06)'
      : '1px solid rgba(0, 0, 0, 0.06)',
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
});

// Modal backdrop styling - OPTIMIZED
export const modalBackdrop = {
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  // REMOVED for performance: backdropFilter: 'blur(4px)',
};

// Search input styling for page variant
export const pageSearchInput = (theme: Theme) => ({
  '& .MuiInputBase-root': {
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'white',
    borderRadius: 3,
    transition: transitions.standard,
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0,0,0,0.04)',
      // REMOVED transform for performance: transform: 'translateY(-1px)',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    },
    '&.Mui-focused': {
      backgroundColor: theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'white',
      // REMOVED transform for performance: transform: 'translateY(-1px)',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.2)'
      : 'rgba(0,0,0,0.23)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.3)'
      : 'rgba(0,0,0,0.87)',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'primary.main',
  },
});

// Search input styling for topbar variant
export const topbarSearchInput = (theme: Theme) => ({
  '& .MuiInputBase-root': {
    color: 'white',
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    // REMOVED for performance: backdropFilter: 'blur(10px)',
    borderRadius: 3,
    transition: transitions.standard,
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      // REMOVED transform for performance: transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    },
    '&.Mui-focused': {
      backgroundColor: 'rgba(255, 255, 255, 0.25)',
      // REMOVED transform for performance: transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255, 255, 255, 0.3)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255, 255, 255, 0.5)',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255, 255, 255, 0.7)',
  },
});

// Gain/loss color helper - matches Portfolio component exactly
export const getGainLossColor = (value: number | undefined | null) => {
  if (value === undefined || value === null || isNaN(value)) {
    return '#6B7280'; // Gray for neutral/null values
  }
  if (value > 0) return '#22C55E'; // Green for positive values
  if (value < 0) return '#EF4444'; // Red for negative values
  return '#6B7280'; // Gray for zero values
};

// Grey flat UI card background - NO shadows or blur
export const portfolioCardBackground = (theme: Theme) => ({
  background: theme.palette.mode === 'dark'
    ? '#1a1a1a'    // Grey flat dark primary - matches flatUIColors.background
    : '#ffffff',   // Pure white - matches flatUIColors.background
  border: theme.palette.mode === 'dark'
    ? '1px solid rgba(255, 255, 255, 0.1)'
    : '1px solid rgba(0, 0, 0, 0.1)',
  borderRadius: 0, // Sharp corners
  boxShadow: 'none',
});

// Subtle background for indicators and chips
export const subtleBackground = (theme: Theme) => ({
  backgroundColor: theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.04)'
    : 'rgba(0, 0, 0, 0.02)',
});

// Metric label styling (from Portfolio component)
export const metricLabelStyle = {
  color: 'text.secondary',
  fontWeight: 500,
  fontSize: '0.875rem',
  letterSpacing: '0.5px',
  textTransform: 'uppercase' as const,
  mb: 1,
};

// Typography color helpers (font sizes and weights applied directly in components)
export const heroTextColor = (theme: Theme) => ({
  color: theme.palette.mode === 'dark' ? '#ffffff' : '#1d1d1f',
});

export const sectionTitleColor = (theme: Theme) => ({
  color: theme.palette.mode === 'dark' ? '#ffffff' : '#1d1d1f',
});

export const metricLabel = {
  color: 'text.secondary',
  fontWeight: 500,
  fontSize: '0.875rem',
  letterSpacing: '0.5px',
  textTransform: 'uppercase' as const,
  mb: 1,
};

// Spacing helpers
export const cardPadding = { p: 3 };
export const sectionSpacing = { mb: 4 };
export const heroSpacing = { py: 6 };

// TopBar specific styling
export const topbarBackground = (theme: Theme) => ({
  background: theme.palette.mode === 'dark'
    ? '#2a2a2a'  // Grey flat UI dark secondary
    : '#22c55e', // Primary green for light mode
  color: 'white',
  marginBottom: 0,
  borderRadius: 0,
  overflow: 'hidden',
  position: 'relative',
  boxShadow: 'none', // Flat UI - no shadows
  // No pseudo-elements for flat design
});

// TopBar button styling - Flat UI
export const topbarButton = (theme: Theme) => ({
  background: 'rgba(255,255,255,0.1)', // Flat background, no gradients
  borderRadius: 0, // Sharp corners
  padding: '8px 16px',
  transition: 'background-color 0.2s linear, border-color 0.2s linear',
  fontWeight: 500,
  border: '1px solid rgba(255,255,255,0.2)',
  boxShadow: 'none',
  '&:hover': {
    background: 'rgba(255,255,255,0.2)', // Flat hover state
    borderColor: 'rgba(255,255,255,0.3)',
    boxShadow: 'none',
  },
  '&:disabled': {
    background: 'rgba(255,255,255,0.05)',
    color: 'rgba(255,255,255,0.4)',
    borderColor: 'rgba(255,255,255,0.1)',
  },
});

// TopBar icon button styling - OPTIMIZED
export const topbarIconButton = {
  transition: transitions.standard,
  '&:hover': {
    // REMOVED transform for performance: transform: 'translateY(-2px)',
    color: 'rgba(255,255,255,0.8)',
    opacity: 0.9,
  },
};

// Animation presets
export const fadeInUp = {
  '@keyframes fadeInUp': {
    from: {
      opacity: 0,
      transform: 'translateY(30px)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
  animation: 'fadeInUp 0.6s ease-out',
};

export const pulse = {
  '@keyframes pulse': {
    '0%': {
      transform: 'scale(1)',
    },
    '50%': {
      transform: 'scale(1.05)',
    },
    '100%': {
      transform: 'scale(1)',
    },
  },
  animation: 'pulse 2s infinite',
};